import { InvalidValueDefinitionError } from "./errors.model";
import type { Member, MemberId } from "./member-identity";

// ============================================================
// Value objects — what a definition asks for, wherever it asks
//
// An observation component, an order parameter, an exam finding and a
// questionnaire answer all ask the author "what kind of value is this", and
// they all get the same answer set. Each value object OWNS ITS OWN RULES ONCE
// — min <= max, options non-empty, unit non-blank, step divides the range —
// regardless of which model composes it.
//
// CONSTRAINTS HANG OFF THE VALUE OBJECT, NEVER BESIDE IT. A `unit` field next
// to a `kind: "Text"` is an unreachable combination somebody has to remember
// to reject; a `unit` inside `Quantity` cannot be expressed at all.
//
// EACH MODEL COMPOSES ITS OWN UNION, and the divergence is a correctness
// constraint rather than taste. `Observation.value[x]` cannot hold an
// attachment, so a shared union would let an author build a diagnostic
// component with nowhere to persist. Per-model unions make that
// UNREPRESENTABLE rather than rejected at runtime: no check to write, no check
// to forget, and greppable — someone asking "why can't I put a photo on a
// diagnostic" finds the union rather than an error message.
//
// Cost, accepted: an author moving between catalog screens sees different
// available types with no visible reason. The reason is real but invisible —
// it is what the destination resource can store.
//
// NOT INCLUDED: `Range`, `Ratio`, `SampledData`, `Period` — legal FHIR, none
// needed, each a branch in every mapper and every control. `dateTime` and
// `time` are deliberately out of the measurement set: a measurement's time is
// `Observation.effectiveDateTime`, which every observation already has, and
// including them gives two ways to record when something happened. Adding any
// of them later is a cheap revision.
// ============================================================

/**
 * One selectable option. `id` is system-assigned like every other structural
 * member — the owning aggregate mints it through `assignMemberIds`, so it is
 * absent on the way in and present once the aggregate has seen it.
 *
 * OPTION LABELS AND DETAILS ARE SUBSTANTIVE, not presentation. Correcting a label from
 * `mg` to `mcg` changes what every past recorded value meant, so editing one
 * mints a revision; it is not a correction.
 */
export interface ChoiceOption extends Member {
  label: string;
  detail?: string;
  points?: number;
}

/** Whether one selection is allowed or several. */
export type ChoiceCardinality = "Single" | "Multiple";

/**
 * A measured amount. The unit is OPTIONAL so this also covers a plain decimal
 * — "how many hours did you sleep" needs no unit.
 */
/**
 * A unit of measure, held as both halves at once.
 *
 * `code` is a UCUM expression and is the IDENTITY — it is what two quantities
 * are compared on, and the only spelling a converter or a reference range can
 * act on. UCUM is case-sensitive (`S` is siemens, `s` is second) and its
 * non-metric atoms are bracketed, so an inch is `[in_i]` and a pound is
 * `[lb_av]`. Things that are not really units ride an annotation: a percentile
 * is `{percentile}`, which is dimensionless by construction and therefore
 * never converts against anything.
 *
 * `display` is what the clinician reads, because nobody wants `[in_i]` beside
 * a field. It carries no meaning and two definitions may spell it differently.
 *
 * The system is not stored. It is always UCUM, and the mapper owns FHIR facts.
 */
export interface UnitValue {
  code: string;
  display: string;
}

interface QuantityValue {
  kind: "Quantity";
  /** Platform-owned presentation hint; answers remain seconds. */
  display?: "duration";
  unit?: UnitValue;
  min?: number;
  max?: number;
  /** Increment the value must land on, counted from `min`. */
  step?: number;
}

interface IntegerValue {
  kind: "Integer";
  min?: number;
  max?: number;
  /** Increment the value must land on, counted from `min`, or from 0 when there is none. */
  step?: number;
}

export interface ChoiceValue {
  kind: "Choice";
  options: readonly ChoiceOption[];
  cardinality: ChoiceCardinality;
  /** Whether the recorder may type something the options do not offer. */
  allowFreeText: boolean;
}

interface BooleanValue {
  kind: "Boolean";
}

/**
 * How a text answer is entered. Absent is a paragraph. Email and Phone are
 * single lines with a shape the answer must have; they stay Text because the
 * answer is still a string, and a kind of their own would only re-spell that
 * through every kind switch.
 */
export type TextFormat = "SingleLine" | "Email" | "Phone";

const TEXT_FORMATS: ReadonlySet<string> = new Set<TextFormat>(["SingleLine", "Email", "Phone"]);

interface TextValue {
  kind: "Text";
  maxLength?: number;
  format?: TextFormat;
}

interface DateValue {
  kind: "Date";
}

interface AttachmentValue {
  kind: "Attachment";
}

interface UrlValue {
  kind: "Url";
}

/**
 * Everything an `Observation.value[x]` can hold that we use. The union behind
 * every diagnostic component, treatment component and configuration parameter
 * — they are one set, named three ways below so each model's declaration reads
 * in its own vocabulary and can diverge later without a sweep.
 */
type MeasurementValue = QuantityValue | IntegerValue | ChoiceValue | BooleanValue | TextValue;

export type DiagnosticComponentValue = MeasurementValue;
export type TreatmentComponentValue = MeasurementValue;
/** What a device kind, diagnostic or treatment parameter asks for. */
export type ParameterValue = MeasurementValue;

/** A patient-facing answer. The measurement set plus what a form can collect that an observation cannot hold. */
export type QuestionnaireAnswerValue = MeasurementValue | DateValue | AttachmentValue | UrlValue;

/** Every value object there is. For code that handles all of them — mappers, and nothing else. */
export type CatalogValue = QuestionnaireAnswerValue;

/**
 * Validate a value object against its own rules. Called by every aggregate
 * that holds one, at construction and on every setter, so the rules live here
 * once instead of once per model.
 *
 * `where` names the member the value hangs off, because "min must not exceed
 * max" with no location is unactionable in a definition with forty members.
 */
export function validateValue(value: CatalogValue, where: { memberId?: MemberId; collection?: string } = {}): void {
  switch (value.kind) {
    case "Quantity":
      validateQuantity(value, where);
      return;
    case "Integer":
      validateInteger(value, where);
      return;
    case "Choice":
      validateChoice(value, where);
      return;
    case "Text":
      validateText(value, where);
      return;
    default:
      // Boolean, Date, Attachment and Url carry nothing, so there is nothing
      // that can be wrong about them. No default case to forget when one
      // gains a constraint — it gets a branch above and this stays honest.
      return;
  }
}

function validateQuantity(value: QuantityValue, where: Where): void {
  if (value.unit !== undefined) validateUnit(value.unit, where);
  assertBounds(value.min, value.max, where);
  if (value.step !== undefined) {
    if (!(value.step > 0)) {
      throw new InvalidValueDefinitionError(`A quantity's step must be greater than zero, got ${value.step}`, where);
    }
    // A step that does not divide the range makes the maximum unreachable —
    // 0..10 by 3 tops out at 9, and the author who typed 10 meant 10. Float
    // division never lands exactly, hence the tolerance.
    if (value.min !== undefined && value.max !== undefined) {
      const steps = (value.max - value.min) / value.step;
      if (Math.abs(steps - Math.round(steps)) > 1e-9) {
        throw new InvalidValueDefinitionError(
          `A quantity's step of ${value.step} does not divide the range ${value.min}–${value.max}`,
          where,
        );
      }
    }
  }
}

function validateUnit(unit: UnitValue, where: Where): void {
  if (unit.code.trim() === "") {
    throw new InvalidValueDefinitionError("A unit's UCUM code must not be blank", where);
  }
  // No UCUM expression contains whitespace, so a space is always either a
  // display string that reached the code field or a typo — and either one
  // silently stops matching the unit it was meant to be.
  if (/\s/.test(unit.code)) {
    throw new InvalidValueDefinitionError(`A unit's UCUM code must not contain whitespace, got '${unit.code}'`, where);
  }
  if (unit.display.trim() === "") {
    throw new InvalidValueDefinitionError("A unit's display must not be blank", where);
  }
}

function validateInteger(value: IntegerValue, where: Where): void {
  for (const [field, bound] of [
    ["min", value.min],
    ["max", value.max],
  ] as const) {
    if (bound !== undefined && !Number.isInteger(bound)) {
      throw new InvalidValueDefinitionError(`An integer's ${field} must be a whole number, got ${bound}`, where);
    }
  }
  assertBounds(value.min, value.max, where);
  if (value.step === undefined) return;
  if (!Number.isInteger(value.step) || value.step <= 0) {
    throw new InvalidValueDefinitionError(
      `An integer's step must be a positive whole number, got ${value.step}`,
      where,
    );
  }
  if (value.min !== undefined && value.max !== undefined && (value.max - value.min) % value.step !== 0) {
    throw new InvalidValueDefinitionError(
      `An integer's step of ${value.step} does not divide the range ${value.min}–${value.max}`,
      where,
    );
  }
}

function validateChoice(value: ChoiceValue, where: Where): void {
  if (value.cardinality !== "Single" && value.cardinality !== "Multiple") {
    throw new InvalidValueDefinitionError(`A choice has an unknown cardinality '${String(value.cardinality)}'`, where);
  }
  if (typeof value.allowFreeText !== "boolean") {
    throw new InvalidValueDefinitionError("A choice must declare whether free text is allowed", where);
  }
  if (value.options.length === 0) {
    throw new InvalidValueDefinitionError("A choice must offer at least one option", where);
  }
  const ids = new Set<string>();
  for (const option of value.options) {
    if (option.points !== undefined && !Number.isInteger(option.points)) {
      throw new InvalidValueDefinitionError("Choice option points must be an integer", where);
    }
    if (option.label.trim() === "") {
      throw new InvalidValueDefinitionError("A choice option must have a label", where);
    }
    if (option.id !== undefined) {
      if (ids.has(option.id)) {
        throw new InvalidValueDefinitionError(`Choice option '${option.id}' appears twice`, {
          ...where,
          memberId: option.id,
        });
      }
      ids.add(option.id);
    }
  }
}

function validateText(value: TextValue, where: Where): void {
  if (value.format !== undefined && !TEXT_FORMATS.has(value.format)) {
    throw new InvalidValueDefinitionError(`Unknown text format '${value.format}'`, where);
  }
  if (value.maxLength !== undefined && (!Number.isInteger(value.maxLength) || value.maxLength < 1)) {
    throw new InvalidValueDefinitionError(
      `A text value's maxLength must be a positive whole number, got ${value.maxLength}`,
      where,
    );
  }
}

type Where = { memberId?: MemberId; collection?: string };

function assertBounds(min: number | undefined, max: number | undefined, where: Where): void {
  if (min !== undefined && max !== undefined && min > max) {
    throw new InvalidValueDefinitionError(`min (${min}) must not exceed max (${max})`, where);
  }
}

const MEASUREMENT_KINDS: ReadonlySet<CatalogValue["kind"]> = new Set([
  "Quantity",
  "Integer",
  "Choice",
  "Boolean",
  "Text",
]);

/**
 * Narrow a hydrated value to the measurement set. For mappers reading storage,
 * which is the only place a value's kind is not already known statically.
 */
export function isMeasurementValue(value: CatalogValue): value is MeasurementValue {
  return MEASUREMENT_KINDS.has(value.kind);
}

export function assertMeasurementValue(value: CatalogValue, where: Where = {}): MeasurementValue {
  if (!isMeasurementValue(value)) {
    throw new InvalidValueDefinitionError(`A ${value.kind} value cannot be stored as an observation`, where);
  }
  return value;
}

export function assertChoiceValue(value: CatalogValue, where: Where = {}): ChoiceValue {
  if (value.kind !== "Choice") {
    throw new InvalidValueDefinitionError(`Expected a choice, got ${value.kind}`, where);
  }
  return value;
}

// ============================================================
// Answered values — what a parameter was actually set to
//
// Everything above DECLARES what a value may be; this is the answer. Both a
// protocol occurrence and an order carry these, which is why they live beside
// the declarations rather than in either model: one shape, so materialization
// copies an answer across without translating it.
//
// TYPED, NOT `{ code, display }`. 40 joules is a quantity with a unit. The
// current occurrence shape fakes every parameter as a code/display pair, which
// is why numeric knobs do not really work.
//
// NO LABEL STORED. The definition holds the declaration, the answer holds only
// the answer, so there is one place to read what a parameter is called and it
// cannot disagree with itself.
// ============================================================

interface QuantityAnswer {
  kind: "Quantity";
  value: number;
  unit?: UnitValue;
}

interface IntegerAnswer {
  kind: "Integer";
  value: number;
}

/** Selected option identities, never labels — a relabelled option must not change what was answered. */
interface ChoiceAnswer {
  kind: "Choice";
  selected: readonly MemberId[];
  /** Only meaningful where the declaration set `allowFreeText`. */
  freeText?: string;
}

interface BooleanAnswer {
  kind: "Boolean";
  value: boolean;
}

interface TextAnswer {
  kind: "Text";
  value: string;
}

/** One answer per declared value kind. Mirrors `MeasurementValue`, which is the set a parameter can declare. */
export type AnsweredValue = QuantityAnswer | IntegerAnswer | ChoiceAnswer | BooleanAnswer | TextAnswer;

/**
 * Which definition a parameter answer belongs to. A procedure's effective
 * parameter set is its own plus every device kind it requires, and the two
 * stay in SEPARATE NAMESPACES — a treatment's `p-1` and its laser's `p-1`
 * cannot collide, so nothing has to be checked for a clash.
 */
export type ParameterSource = "self" | MemberId;

/**
 * One answer to one declared parameter. ABSENT MEANS UNANSWERED — there are no
 * null entries, so "did anyone set this" is answered by presence rather than
 * by inspecting a value.
 */
export interface ParameterAnswer {
  source: ParameterSource;
  parameterId: MemberId;
  value: AnsweredValue;
}

/**
 * Whether two answers say the same thing. Every logging aggregate needs it: a
 * setter handed the value it already holds is a no-op, and a no-op must produce
 * no change-log entry. Structural rather than referential, because the incoming
 * answer is always a fresh object off the wire.
 */
export function answersEqual(a: AnsweredValue | undefined, b: AnsweredValue | undefined): boolean {
  if (a === undefined || b === undefined) return a === b;
  if (a.kind !== b.kind) return false;
  if (a.kind === "Choice" && b.kind === "Choice") {
    return (
      a.freeText === b.freeText &&
      a.selected.length === b.selected.length &&
      a.selected.every((id, index) => id === b.selected[index])
    );
  }
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * Explain why an answered value does not satisfy its declaration. The owning
 * aggregate turns the explanation into its own domain error, keeping this
 * value-object module independent of orders, protocols, and questionnaires.
 */
export function answeredValueProblem(declaration: ParameterValue, answer: AnsweredValue): string | undefined {
  if (declaration.kind !== answer.kind) return `expects ${declaration.kind}, got ${answer.kind}`;

  switch (declaration.kind) {
    case "Quantity": {
      const quantity = answer as QuantityAnswer;
      if (!Number.isFinite(quantity.value)) return "requires a finite quantity";
      if (declaration.unit !== undefined && quantity.unit?.code !== declaration.unit.code) {
        return `expects unit '${declaration.unit.code}', got '${quantity.unit?.code ?? "none"}'`;
      }
      if (declaration.min !== undefined && quantity.value < declaration.min)
        return `must be at least ${declaration.min}`;
      if (declaration.max !== undefined && quantity.value > declaration.max)
        return `must be at most ${declaration.max}`;
      if (declaration.step !== undefined && declaration.min !== undefined) {
        const steps = (quantity.value - declaration.min) / declaration.step;
        const tolerance = Math.max(
          1e-9,
          (4 * Number.EPSILON * Math.max(Math.abs(quantity.value), Math.abs(declaration.min))) / declaration.step,
        );
        if (Math.abs(steps - Math.round(steps)) > tolerance) return `must use increments of ${declaration.step}`;
      }
      return undefined;
    }
    case "Integer": {
      const integer = answer as IntegerAnswer;
      if (!Number.isInteger(integer.value)) return "requires a whole number";
      if (declaration.min !== undefined && integer.value < declaration.min)
        return `must be at least ${declaration.min}`;
      if (declaration.max !== undefined && integer.value > declaration.max) return `must be at most ${declaration.max}`;
      if (declaration.step !== undefined && (integer.value - (declaration.min ?? 0)) % declaration.step !== 0)
        return `must use increments of ${declaration.step}`;
      return undefined;
    }
    case "Choice": {
      const choice = answer as ChoiceAnswer;
      const allowed = new Set(declaration.options.map((option) => option.id).filter(Boolean));
      if (new Set(choice.selected).size !== choice.selected.length) return "cannot select the same option twice";
      if (choice.selected.some((id) => !allowed.has(id))) return "selects an option the declaration does not contain";
      if (declaration.cardinality === "Single" && choice.selected.length > 1) return "allows only one selection";
      if (choice.freeText !== undefined && !declaration.allowFreeText) return "does not allow free text";
      if (choice.selected.length === 0 && choice.freeText === undefined) return "requires a selection or free text";
      return undefined;
    }
    case "Text": {
      const text = answer as TextAnswer;
      if (declaration.maxLength !== undefined && text.value.length > declaration.maxLength) {
        return `must be at most ${declaration.maxLength} characters`;
      }
      return undefined;
    }
    case "Boolean":
      return undefined;
  }
}
